import { FormEvent, useEffect, useState } from "react";
import type { AffiliateProductProgram, PepAffiliateSettings } from "@itaas/pep-contracts";
import { fetchPepSettings, savePepSettings } from "./admin-api";

function ProgramEditor({
  productSlug,
  program,
  onChange,
}: {
  productSlug: string;
  program: AffiliateProductProgram;
  onChange: (next: AffiliateProductProgram) => void;
}) {
  return (
    <div className="border rounded p-3 mb-3 bg-body-tertiary">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="fw-semibold text-uppercase small">{productSlug}</span>
        <div className="form-check form-switch mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            checked={program.enabled}
            onChange={(e) => onChange({ ...program, enabled: e.target.checked })}
            id={`prog-${productSlug}-on`}
          />
          <label className="form-check-label small" htmlFor={`prog-${productSlug}-on`}>
            Activé
          </label>
        </div>
      </div>
      <div className="row g-2">
        <div className="col-md-6">
          <label className="form-label small">Nom affiché (FR)</label>
          <input
            className="form-control form-control-sm"
            value={program.displayNameFr}
            onChange={(e) => onChange({ ...program, displayNameFr: e.target.value })}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label small">Accroche (FR)</label>
          <input
            className="form-control form-control-sm"
            value={program.taglineFr}
            onChange={(e) => onChange({ ...program, taglineFr: e.target.value })}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small">Commission cash %</label>
          <input
            type="number"
            className="form-control form-control-sm"
            min={0}
            max={50}
            value={program.commissionPercent}
            onChange={(e) =>
              onChange({ ...program, commissionPercent: parseInt(e.target.value, 10) || 0 })
            }
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small">Réduction facture établ. %</label>
          <input
            type="number"
            className="form-control form-control-sm"
            min={0}
            max={50}
            value={program.institutionReferralDiscountPercent ?? 10}
            onChange={(e) =>
              onChange({
                ...program,
                institutionReferralDiscountPercent: parseInt(e.target.value, 10) || 10,
              })
            }
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small">Override two-tier %</label>
          <input
            type="number"
            className="form-control form-control-sm"
            min={0}
            max={50}
            value={program.secondTierCommissionPercent ?? 0}
            onChange={(e) =>
              onChange({
                ...program,
                secondTierCommissionPercent: parseInt(e.target.value, 10) || 0,
              })
            }
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small">Min. paiement USD</label>
          <input
            type="number"
            className="form-control form-control-sm"
            min={0}
            value={program.minPayoutUsd}
            onChange={(e) =>
              onChange({ ...program, minPayoutUsd: parseInt(e.target.value, 10) || 0 })
            }
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small">Modèle commission</label>
          <select
            className="form-select form-select-sm"
            value={program.commissionModel}
            onChange={(e) =>
              onChange({
                ...program,
                commissionModel: e.target.value as AffiliateProductProgram["commissionModel"],
              })
            }
          >
            <option value="first_payment">Premier paiement</option>
            <option value="recurring">Récurrent</option>
          </select>
        </div>
        <div className="col-12">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              checked={program.allowInstitutionReferrers}
              onChange={(e) =>
                onChange({ ...program, allowInstitutionReferrers: e.target.checked })
              }
              id={`prog-${productSlug}-inst`}
            />
            <label className="form-check-label small" htmlFor={`prog-${productSlug}-inst`}>
              Écoles, hôpitaux et entreprises peuvent parrainer (réduction facture)
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminAffiliateSettingsPage() {
  const [affiliate, setAffiliate] = useState<PepAffiliateSettings | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPepSettings()
      .then((s) => setAffiliate(s.affiliate ?? null))
      .catch(() => setError("Impossible de charger les paramètres affiliation."));
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!affiliate) return;
    setError(null);
    setSaved(false);
    try {
      await savePepSettings({ affiliate });
      setSaved(true);
    } catch {
      setError("Échec de l'enregistrement.");
    }
  }

  if (!affiliate) {
    return <p className="text-secondary">Chargement…</p>;
  }

  const programSlugs = Object.keys(affiliate.programs).sort();

  return (
    <>
      <h1 className="h4 fw-bold mb-1">Paramètres affiliation</h1>
      <p className="text-secondary small mb-4">
        Commissions cash (particuliers, mobile money / agence) et réductions facture pour
        établissements parraineurs.
      </p>

      <form onSubmit={onSubmit}>
        <div className="card border-0 shadow-sm p-4 mb-4">
          <div className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="aff-master"
              checked={affiliate.enabled}
              onChange={(e) => setAffiliate({ ...affiliate, enabled: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="aff-master">
              Programme ambassadeur actif
            </label>
          </div>
          <div className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="aff-email"
              checked={affiliate.emailVerificationRequired ?? true}
              onChange={(e) =>
                setAffiliate({ ...affiliate, emailVerificationRequired: e.target.checked })
              }
            />
            <label className="form-check-label" htmlFor="aff-email">
              Vérification email obligatoire (inscription et connexion OTP)
            </label>
          </div>
          <div className="form-check form-switch mb-3 opacity-50">
            <input
              className="form-check-input"
              type="checkbox"
              id="aff-phone"
              checked={affiliate.phoneVerificationRequired ?? false}
              disabled
              readOnly
            />
            <label className="form-check-label" htmlFor="aff-phone">
              Vérification SMS (désactivé — non supporté en RDC)
            </label>
          </div>
          <div className="row g-2">
            <div className="col-auto">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="pay-mm"
                  checked={affiliate.payoutMethods?.mobileMoney ?? true}
                  onChange={(e) =>
                    setAffiliate({
                      ...affiliate,
                      payoutMethods: {
                        ...affiliate.payoutMethods,
                        mobileMoney: e.target.checked,
                        agency: affiliate.payoutMethods?.agency ?? true,
                      },
                    })
                  }
                />
                <label className="form-check-label small" htmlFor="pay-mm">
                  Paiement Mobile Money
                </label>
              </div>
            </div>
            <div className="col-auto">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="pay-agency"
                  checked={affiliate.payoutMethods?.agency ?? true}
                  onChange={(e) =>
                    setAffiliate({
                      ...affiliate,
                      payoutMethods: {
                        mobileMoney: affiliate.payoutMethods?.mobileMoney ?? true,
                        agency: e.target.checked,
                      },
                    })
                  }
                />
                <label className="form-check-label small" htmlFor="pay-agency">
                  Retrait en agence ITaaS
                </label>
              </div>
            </div>
          </div>
        </div>

        <h2 className="h6 fw-bold mb-3">Programmes par produit</h2>
        {programSlugs.map((slug) => (
          <ProgramEditor
            key={slug}
            productSlug={slug}
            program={affiliate.programs[slug]}
            onChange={(next) =>
              setAffiliate({
                ...affiliate,
                programs: { ...affiliate.programs, [slug]: next },
              })
            }
          />
        ))}

        {error && <div className="alert alert-danger">{error}</div>}
        {saved && <div className="alert alert-success py-2">Paramètres affiliation enregistrés.</div>}
        <button type="submit" className="btn btn-primary">
          Enregistrer l&apos;affiliation
        </button>
      </form>
    </>
  );
}
